import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { SuccessPageState } from '../../types/navigation';
import CommonPageWrapper from '../../components/layouts/CommonPageWrapper';
import { connectionService, profileService } from '../../services';
import { UserStats } from '../../types/profile';
import UserStatsDisplay from '../../components/misc/UserStatsDisplay';
import useAuth from '../../hooks/useAuth';
import ConnectionOverviewScreen from '../authenticated/app/connections/ConnectionDetailsPart';
import CommunityOverviewScreen from '../../components/overviews/CommunityOverviewScreen';
import ContactsOverviewScreen from '../../components/overviews/ContactsOverviewScreen';
import useEffectAfterFirstRender from '../../hooks/useEffectAfterFirstRender';
import Button from '../../components/misc/Button';
import { PersonalConnection } from '../../types/misc';

export default function SuccessPage(): JSX.Element {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const type = params.get('type');
  const paramTranslationKey =
    type === 'payment' ? 'page.common.success.message.payment' : 'page.common.success.message.default';
  const auth = useAuth();
  const { requesterId, networkInvite, translationKey, translationOptions } = (location.state as SuccessPageState) ?? {};

  const [stats, setStats] = React.useState<UserStats>();
  const [connection, setConnection] = React.useState<PersonalConnection | null>(null);
  const translationKeyFinal = translationKey || paramTranslationKey;

  useEffect(() => {
    if (!location.state && !type) navigate('/');
  }, []);

  useEffect(() => {
    if (!auth) return;

    profileService.getUserStatistics().then((data) => {
      setStats(data);
    });
  }, [JSON.stringify(auth)]);

  useEffect(() => {
    if (!requesterId) return;

    connectionService.getMyConnections().then((connections) => {
      const matchedConnection = connections.find((c) => c.id.toString() === requesterId);
      if (matchedConnection) {
        setConnection(matchedConnection);
      }
    });
  }, [requesterId]);

  useEffectAfterFirstRender(() => {
    if (!auth) {
      navigate('/');
      toast.error(t('toast.error.general.loggedOut'));
    }
  }, [JSON.stringify(auth)]);

  return (
    <CommonPageWrapper>
      <CommonPageWrapper.Header>
        <div className="relative flex h-full w-full items-center justify-center gap-2.5 rounded-[40px] bg-primary-300 p-5">
          {connection && <ConnectionOverviewScreen connection={connection} />}
          <CommunityOverviewScreen invite={networkInvite} />
          {!requesterId && !networkInvite && <ContactsOverviewScreen />}
        </div>
      </CommonPageWrapper.Header>
      <CommonPageWrapper.Title>
        {t('page.common.success.title', { name: auth?.alias.split('+')[0] })}
      </CommonPageWrapper.Title>
      <CommonPageWrapper.Message>
        {translationKeyFinal ? t(translationKeyFinal, translationOptions || undefined) : ''}
      </CommonPageWrapper.Message>

      <div className="flex h-full w-full flex-1 justify-between">
        <UserStatsDisplay stats={stats} />
        <Link to="/">
          <Button variant="primary">{t('general.close')}</Button>
        </Link>
      </div>
    </CommonPageWrapper>
  );
}
